import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import {
  ArrowLeft,
  ChevronRight,
  Download,
  FolderOpen,
  GitFork,
  GitPullRequestArrow,
  Link2,
  LoaderCircle,
  Lock,
  Search,
} from 'lucide-react';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { errorMessage } from '@/lib/error-message';
import { useRouter } from '@/lib/navigation';
import { pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';
import {
  fetchSourceControlRepositories,
  type SourceControlConnectorState,
  type SourceControlRepository,
} from '@/lib/source-control-connector';

type AddRepositoryView = 'choose' | 'sourceControl';

const SOURCE_ROW =
  'flex items-center gap-3 rounded-xl bg-control px-4 py-3 text-left transition-colors hover:bg-control/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The forge to clone from, as its plugin names it (GitHub when it is the only one). */
function forgeTitle(state: SourceControlConnectorState | null): string {
  const providers = state?.providers ?? [];
  return providers.length === 1 ? (providers[0]?.title ?? 'a code forge') : 'a code forge';
}

/** `PersoJonas · crowlingo · 30 repositories`: whose repositories, and how many. */
function forgeSummary(state: SourceControlConnectorState | null): string {
  if (state === null) return 'Choose a repository of your connected forges';
  if (!state.connected) return 'Connect your account to choose a repository';
  const owners = [...new Set(state.repositories.map((repository) => repository.owner))];
  const count = state.repositories.length;
  return [...owners.slice(0, 3), `${count} ${count === 1 ? 'repository' : 'repositories'}`].join(
    ' · ',
  );
}

const repositoryKey = (repository: SourceControlRepository) =>
  `${repository.host}/${repository.fullName}`;

/**
 * "Add a repository" (M4: the one dialog everywhere): open a folder of this computer, clone from
 * a code forge, or (a workspace's page) choose one the user already has. Each way is offered only
 * where it applies.
 */
export function AddRepositoryDialog({
  localAvailable,
  onClone,
  onOpenChange,
  onOpenLocal,
  open,
  repositories: owned,
  onChoose,
}: {
  localAvailable: boolean;
  /** Clones a forge repository; without it, the dialog offers no forge. */
  onClone?(repository: SourceControlRepository): Promise<boolean>;
  onOpenChange(open: boolean): void;
  onOpenLocal(): Promise<boolean>;
  open: boolean;
  /** The user's repositories that may be added (a workspace's page): chosen as they are. */
  repositories?: readonly { id: string; name: string }[];
  onChoose?(id: string): void;
}) {
  const router = useRouter();
  const [view, setView] = useState<AddRepositoryView>('choose');
  const [sourceControl, setSourceControl] = useState<SourceControlConnectorState | null>(null);
  const [loading, setLoading] = useState(false);
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reads the latest props when the dialog opens (or what it offers changes), without reopening.
  const loadOnOpen = useEffectEvent((local: boolean) => {
    if (onClone === undefined) return;
    if (!local && owned === undefined) void showSourceControl();
    // Its row says whose repositories it clones, and how many.
    else void loadSourceControl();
  });
  useEffect(() => {
    if (!open) {
      setView('choose');
      setQuery('');
      setError(null);
      return;
    }
    loadOnOpen(localAvailable);
  }, [localAvailable, open]);

  const repositories = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (normalized.length === 0) return sourceControl?.repositories ?? [];
    return (sourceControl?.repositories ?? []).filter((repository) =>
      repository.fullName.toLowerCase().includes(normalized),
    );
  }, [sourceControl?.repositories, query]);

  async function showSourceControl(): Promise<void> {
    setView('sourceControl');
    await loadSourceControl();
  }

  async function loadSourceControl(): Promise<void> {
    if (sourceControl !== null || loading) return;
    setLoading(true);
    setError(null);
    try {
      setSourceControl(await fetchSourceControlRepositories());
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  }

  async function clone(repository: SourceControlRepository): Promise<void> {
    setCloningId(repositoryKey(repository));
    setError(null);
    try {
      if (await onClone?.(repository)) onOpenChange(false);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setCloningId(null);
    }
  }

  return (
    <Dialog onOpenChange={(next) => cloningId === null && onOpenChange(next)} open={open}>
      <DialogContent className="gap-3 rounded-card bg-card p-0 sm:max-w-[30rem]">
        <DialogHeader
          className={
            view === 'choose'
              ? 'px-5 pt-5 pb-1 text-left'
              : 'border-hairline border-b px-5 py-4 text-left'
          }
        >
          <div className="flex items-center gap-2">
            {view === 'sourceControl' && (localAvailable || owned !== undefined) && (
              <Button
                aria-label="Back to repository sources"
                className="-ml-2 size-control-sm"
                onClick={() => {
                  setView('choose');
                  setError(null);
                }}
                size="icon"
                variant="ghost"
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <div>
              <DialogTitle className={view === 'choose' ? 'type-card-title' : 'text-base'}>
                {view === 'choose'
                  ? 'Add a repository'
                  : localAvailable
                    ? 'Clone from a code forge'
                    : 'Open a forge repository'}
              </DialogTitle>
              <DialogDescription className={view === 'choose' ? 'sr-only' : 'mt-1 text-xs'}>
                {view === 'choose'
                  ? 'Open an existing working copy or clone a repository from a code forge.'
                  : localAvailable
                    ? 'Choose a repository, then select the local folder where Crowdex should clone it.'
                    : 'Choose a repository for the new Cloud conversation.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {view === 'choose' ? (
          <div className="grid gap-2.5 px-5 pb-5">
            {owned !== undefined && owned.length > 0 && (
              <ul aria-label="Your repositories" className="grid gap-1">
                {owned.map((repository) => (
                  <li key={repository.id}>
                    <button
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => {
                        onChoose?.(repository.id);
                        onOpenChange(false);
                      }}
                      type="button"
                    >
                      <GitFork aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                      <span className="type-body min-w-0 flex-1 truncate text-foreground">
                        {repository.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {localAvailable && (
              <button
                className={SOURCE_ROW}
                onClick={() =>
                  void onOpenLocal().then((selected) => selected && onOpenChange(false))
                }
                type="button"
              >
                <FolderOpen aria-hidden className="size-5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="type-body block text-foreground">Open a local folder</span>
                  <span className="type-meta block text-muted-foreground">
                    A repository that already exists on this computer
                  </span>
                </span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              </button>
            )}
            {onClone !== undefined && (
              <button className={SOURCE_ROW} onClick={() => void showSourceControl()} type="button">
                <GitPullRequestArrow
                  aria-hidden
                  className="size-5 shrink-0 text-muted-foreground"
                />
                <span className="min-w-0 flex-1">
                  <span className="type-body block text-foreground">
                    Clone from {forgeTitle(sourceControl)}
                  </span>
                  <span className="type-meta block truncate text-muted-foreground">
                    {forgeSummary(sourceControl)}
                  </span>
                </span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              </button>
            )}
            <button
              className="flex items-center gap-3 rounded-xl border border-hairline-strong type-body px-4 py-3 text-left text-muted-foreground transition-colors hover:bg-control/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                onOpenChange(false);
                router.push(pluginsPath());
              }}
              type="button"
            >
              <Link2 aria-hidden className="size-5 shrink-0" />
              Connect another forge (GitLab…)
            </button>
          </div>
        ) : (
          <div className="p-3">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
                <LoaderCircle className="size-4 animate-spin" /> Loading repositories…
              </div>
            )}
            {!loading && sourceControl?.configured === false && (
              <p className="py-8 text-center text-muted-foreground text-sm">
                Install a code forge plugin, such as GitHub, to choose repositories.
              </p>
            )}
            {!loading && sourceControl?.configured === true && !sourceControl.connected && (
              <div className="grid justify-items-center gap-3 py-8 text-center">
                <p className="text-muted-foreground text-sm">
                  Connect your code forge account to choose repositories.
                </p>
                <Button
                  onClick={() => {
                    const provider = sourceControl.providers[0];
                    onOpenChange(false);
                    router.push(
                      provider
                        ? pluginPagePath('installed', provider.name)
                        : pluginsPath('installed'),
                    );
                  }}
                >
                  <GitFork className="size-4" /> Connect
                </Button>
              </div>
            )}
            {sourceControl?.connected === true && (
              <>
                {sourceControl.repositories.length > 5 && (
                  <label
                    className="mb-2 flex h-9 items-center gap-2 rounded-lg border border-border px-3 text-muted-foreground"
                    htmlFor="sourceControl-repository-search"
                  >
                    <Search className="size-3.5" />
                    <Input
                      aria-label="Search repositories"
                      appearance="bare"
                      className="h-auto p-0"
                      id="sourceControl-repository-search"
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search repositories…"
                      value={query}
                    />
                  </label>
                )}
                <div className="max-h-72 space-y-1 overflow-y-auto">
                  {repositories.map((repository) => (
                    <button
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                      disabled={cloningId !== null}
                      key={repositoryKey(repository)}
                      onClick={() => void clone(repository)}
                      type="button"
                    >
                      {cloningId === repositoryKey(repository) ? (
                        <LoaderCircle className="size-4 shrink-0 animate-spin" />
                      ) : repository.private ? (
                        <Lock className="size-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <GitFork className="size-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-sm">
                          {repository.fullName}
                        </span>
                        <span className="block text-muted-foreground text-xs">
                          {repository.private ? 'Private' : 'Public'} · {repository.defaultBranch}
                        </span>
                      </span>
                      <Download className="size-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                  {repositories.length === 0 && (
                    <p className="py-8 text-center text-muted-foreground text-sm">
                      No matching repository.
                    </p>
                  )}
                </div>
              </>
            )}
            {error !== null && <p className="mt-3 text-destructive text-sm">{error}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
